import { ScenePreview } from "@cva/renderer";
import { type DocumentVersion, FORMAT_PRESETS, PRESET_CATEGORY_LABELS } from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { History } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { client, orpc } from "../lib/api";
import { cn, errorMessage, formatDateTime } from "../lib/utils";
import { useEnv } from "./env";
import { useEditorApi } from "./store";

const KIND_LABELS: Record<DocumentVersion["kind"], string> = {
  auto: "Automatisch",
  manual: "Gespeichert",
  restore: "Vor Wiederherstellung",
};

export function VersionsDialog({
  open,
  onOpenChange,
  beforeRestore,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  beforeRestore: () => Promise<unknown>;
}) {
  const env = useEnv();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const versions = useQuery({
    ...orpc.documents.versions.list.queryOptions({
      input: { orgId: env.orgId, documentId: env.documentId },
    }),
    enabled: open,
  });
  const preview = useQuery({
    ...orpc.documents.versions.get.queryOptions({
      input: { orgId: env.orgId, versionId: selected ?? "" },
    }),
    enabled: Boolean(selected),
  });
  const create = useMutation(
    orpc.documents.versions.create.mutationOptions({
      onSuccess: () => {
        setLabel("");
        void versions.refetch();
        toast.success("Version gespeichert");
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const restore = useMutation({
    mutationFn: async (versionId: string) => {
      await beforeRestore();
      // The editor gets the restored state live, like everyone else in the file.
      await client.documents.versions.restore({
        orgId: env.orgId,
        documentId: env.documentId,
        versionId,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
      toast.success("Version wiederhergestellt");
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Versionsverlauf"
        description="Automatisch alle 10 Minuten beim Bearbeiten, dazu benannte Versionen."
        className="max-w-3xl"
      >
        <div className="grid gap-5 md:grid-cols-[1fr_280px]">
          <div>
            <form
              className="mb-3 flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (label.trim()) {
                  void beforeRestore().then(() =>
                    create.mutate({
                      orgId: env.orgId,
                      documentId: env.documentId,
                      label: label.trim(),
                    }),
                  );
                }
              }}
            >
              <Input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Aktuellen Stand benennen, z. B. „Freigabe Kunde“"
              />
              <Button
                type="submit"
                variant="secondary"
                loading={create.isPending}
                disabled={!label.trim() || !env.canEdit}
              >
                Speichern
              </Button>
            </form>
            <ul className="max-h-96 space-y-1 overflow-y-auto">
              {versions.data?.length === 0 ? (
                <li className="flex items-center gap-2 p-3 text-sm text-zinc-500">
                  <History className="size-4" /> Noch keine Versionen.
                </li>
              ) : null}
              {versions.data?.map((version) => (
                <li key={version.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(version.id)}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-zinc-100",
                      selected === version.id && "bg-brand-50 hover:bg-brand-50",
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-zinc-900">
                        {version.label ?? formatDateTime(version.createdAt)}
                      </span>
                      <span className="block text-xs text-zinc-500">
                        {version.label ? `${formatDateTime(version.createdAt)} · ` : ""}
                        {KIND_LABELS[version.kind]}
                        {version.createdBy ? ` · ${version.createdBy.name}` : ""}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-3">
            <div className="flex min-h-48 items-center justify-center rounded-lg bg-zinc-100 p-3">
              {preview.data ? (
                <div className="shadow ring-1 ring-black/5">
                  <ScenePreview
                    document={preview.data.content}
                    width={250}
                    resolveAsset={env.resolveAsset}
                  />
                </div>
              ) : (
                <p className="text-sm text-zinc-500">{selected ? "Lädt …" : "Version auswählen"}</p>
              )}
            </div>
            <Button
              className="w-full"
              disabled={!selected || !env.canEdit}
              loading={restore.isPending}
              onClick={() => selected && restore.mutate(selected)}
            >
              Diese Version wiederherstellen
            </Button>
            <p className="text-xs text-zinc-500">
              Der aktuelle Stand wird vorher als Version gesichert.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Shown when someone else saved the document in the meantime. */
export function TransferDialog({
  open,
  onOpenChange,
  beforeTransfer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  beforeTransfer: () => Promise<unknown>;
}) {
  const env = useEnv();
  const store = useEditorApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const current = store.getState().doc.format.preset;
  const presets = FORMAT_PRESETS.filter(
    (preset) => preset.kind === "design" && preset.id !== current,
  );
  const [presetId, setPresetId] = useState(presets[0]?.id ?? "");
  const transfer = useMutation({
    mutationFn: async () => {
      await beforeTransfer();
      return client.documents.transfer({
        orgId: env.orgId,
        documentId: env.documentId,
        presetId,
      });
    },
    onSuccess: async (document) => {
      await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
      onOpenChange(false);
      toast.success(`„${document.title}“ angelegt`);
      await navigate({
        to: "/o/$orgId/documents/$documentId",
        params: { orgId: env.orgId, documentId: document.id },
      });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const categories = ["social", "print"] as const;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="In anderes Format übertragen"
        description="Legt eine Kopie im gewählten Format an. Inhalte werden proportional skaliert und neu verteilt; das Original bleibt unverändert."
        className="max-w-2xl"
      >
        <div className="space-y-4">
          {categories.map((category) => (
            <div key={category}>
              <p className="mb-2 text-xs font-semibold text-zinc-700">
                {PRESET_CATEGORY_LABELS[category]}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {presets
                  .filter((preset) => preset.category === category)
                  .map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => setPresetId(preset.id)}
                      className={cn(
                        "rounded-lg border px-3 py-2 text-left transition",
                        presetId === preset.id
                          ? "border-brand-500 bg-brand-50 ring-2 ring-brand-500/20"
                          : "border-zinc-200 hover:border-zinc-300",
                      )}
                    >
                      <span className="block truncate text-sm font-medium">{preset.label}</span>
                      <span className="block text-xs text-zinc-500">
                        {preset.print
                          ? `${preset.print.widthMm} × ${preset.print.heightMm} mm`
                          : `${preset.width} × ${preset.height} px`}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            loading={transfer.isPending}
            disabled={!presetId}
            onClick={() => transfer.mutate()}
          >
            Übertragen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
